import React, {Component} from 'react';
import AlertCloseButton from './AlertCloseButton';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {SVGIcon} from '@homeaway/react-svg';
import {ALERT_WARNING__24} from '@homeaway/svg-defs';
import './Alert.less';

/**
 * Alert
 *
 */

class Alert extends Component {
    static propTypes = {
        /** The aria-live value for the alert.
         * 'polite' - indicates that the screen reader should wait until the user is idle before presenting updates to the user
         * 'assertive' may interupt user's flow
         */
        ariaLive: PropTypes.oneOf(['assertive', 'polite']),
        /** Alert heading, wrapped in an <h4> if you want it.  */
        title: PropTypes.string,
        /** Alert message to be formatted in a <p> tag.  You can also provide your own children markup/components */
        msg: PropTypes.node,
        /* CSS classes to apply to the svg. This is commented out since its meant as an internal property. */
        // svgClassName: PropTypes.string,
        /** SVG markup to display for Alert imported from @homeaway/svg-defs module. Please reference individual file in /lib/defs/ */
        svgMarkup: PropTypes.string,
        /** ID name of SVG from an SVG Definition block, the xlink:href of the SVG you want. Explicitly for use with SVG Definitions coming from the icon system in @homeaway/svg-defs */
        svgId: PropTypes.string,
        /** Flag to determine if the Alert has a close button and can be dismissed */
        dismissible: PropTypes.bool,
        /** Flag to hide the flair for less emphasis */
        subtle: PropTypes.bool,
        /** Screen Reader .sr-only text for close button. Defaults to english: "Close" */
        closeText: PropTypes.string,
        /** Optional event handler to fire after an Alert has closed */
        onAlertClosed: PropTypes.func
        /** Background CSS helper for flair on left side of Alert. Internal use only. */
        // flairClassName: PropTypes.string
    };

    static defaultProps = {
        ariaLive: 'polite',
        dismissible: false,
        subtle: false,
        closeText: 'Close',
        svgMarkup: ALERT_WARNING__24,
        flairClassName: 'bg--color-bg-active',
        svgClassName: 'text-muted'
    };

    constructor(props) {
        super(props);

        this.alert = null;

        this.state = {
            dismissed: false,
            visible: true
        };
    }

    removeAlert = () => {
        this.setState({visible: false});

        if (this.props.onAlertClosed) {
            this.props.onAlertClosed();
        }
    };

    dismissAlert = () => {
        this.setState({dismissed: true});
        setTimeout(() => {
            this.removeAlert();
        }, 500);
    };

    // eslint-disable-next-line complexity
    render() {
        const {
            ariaLive,
            title,
            msg,
            svgClassName,
            svgMarkup,
            svgId,
            dismissible,
            closeText,
            className,
            flairClassName,
            onAlertClosed, // eslint-disable-line no-unused-vars
            subtle,
            ...rest
        } = this.props; // eslint-disable-line no-unused-vars
        const {dismissed, visible} = this.state;
        const alertClass = classNames(
            'Alert',
            'border--default',
            {
                'Alert--dismissible': dismissible,
                fade: dismissible,
                in: !dismissed
            },
            className
        );
        const flairClass = classNames('Alert__flair', flairClassName);
        const svgClass = classNames('Alert__svg', 'inline-svg', svgClassName);

        let finalIconMarkup;

        if (svgId) {
            finalIconMarkup = <SVGIcon className={svgClass} id={svgId} />;
        } else if (svgMarkup) {
            finalIconMarkup = (
                <SVGIcon className={svgClass} markup={svgMarkup} />
            );
        }

        if (visible) {
            return (
                <div
                    ref={
                        /* istanbul ignore next */
                        (e) => {
                            this.alert = e;
                        }
                    }
                    aria-live={ariaLive}
                    role="alert"
                    className={alertClass}
                    {...rest}
                >
                    {!subtle && <div className={flairClass} />}
                    {finalIconMarkup}
                    {dismissible && (
                        <AlertCloseButton
                            closeText={closeText}
                            onClick={this.dismissAlert}
                        />
                    )}
                    {title && (
                        <p>
                            <strong>{title}</strong>
                        </p>
                    )}
                    {msg && <p>{msg}</p>}
                    {this.props.children}
                </div>
            );
        }
        return false;
    }
}

export default Alert;
